import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Avatar,
  Card,
  CardContent,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Switch,
  Chip,
  Alert,
} from '@mui/material';
import {
  AccessTime as AccessTimeIcon,
  Lock as LockIcon,
  ErrorOutline as ErrorOutlineIcon,
} from '@mui/icons-material';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../animations/variants';
import { toast } from 'react-toastify';
import apiService from '../services/api';
import { getCustomerIdFromSubdomain } from '../utils/constants';

// Theme color for JIT Access page
const accentColor = '#16A085';
const accentColorLight = '#E8F6F3';
const accentColorHover = '#138D75';

const dialogPaperProps = {
  sx: {
    borderRadius: '12px',
    border: '1px solid #E5E5E5',
    backgroundColor: '#FFFFFF',
  },
};

// Consistent button styles
const buttonStyles = {
  primary: (color, hoverColor) => ({
    backgroundColor: color,
    fontFamily: 'Metropolis, sans-serif',
    fontWeight: 600,
    textTransform: 'none',
    borderRadius: '6px',
    px: 3,
    '&:hover': { backgroundColor: hoverColor },
    '&:disabled': {
      backgroundColor: '#E5E5E5',
      color: '#999999',
    },
  }),
  cancel: {
    borderColor: '#E5E5E5',
    color: '#666666',
    fontFamily: 'Metropolis, sans-serif',
    fontWeight: 600,
    textTransform: 'none',
    borderRadius: '6px',
    px: 3,
    '&:hover': {
      borderColor: '#999999',
      backgroundColor: '#F9FBFD',
    },
  },
};

const cardStyle = {
  backgroundColor: '#FFFFFF',
  border: '1px solid #E5E5E5',
  borderRadius: '8px',
};

const bodyTextSx = {
  fontFamily: 'Metropolis, sans-serif',
  fontSize: '0.875rem',
  color: '#666666',
};

// Bullet used by the disable-confirmation dialog. Kept as a component so every line
// in that dialog shares one indent and one text style — the copy there is the part an
// admin reads before making an irreversible change, so it must not drift.
const ConsequenceItem = ({ children }) => (
  <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', mb: 1 }}>
    <Box
      component="span"
      sx={{
        width: 5,
        height: 5,
        borderRadius: '50%',
        backgroundColor: '#999999',
        mt: '0.5rem',
        flexShrink: 0,
      }}
    />
    <Typography sx={{ ...bodyTextSx, fontSize: '0.85rem' }}>{children}</Typography>
  </Box>
);

const JITSettings = () => {
  const customerId = getCustomerIdFromSubdomain();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [jitEnabled, setJitEnabled] = useState(null);
  // Set when the backend answers 403 to the role check on /jit/config. The switch is
  // never rendered in that case: a below-sso_admin admin cannot read the flag either,
  // so a switch would be both dead and showing a made-up value.
  const [forbidden, setForbidden] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);

  // A 403 carrying code 'jit_disabled' is the JIT gate, not the role check — the two
  // are told apart here so an admin who lacks the role is never told JIT is off, and
  // an admin whose tenant is off is never told they lack the role.
  const isRoleForbidden = (err) => err?.status === 403 && err?.code !== 'jit_disabled';

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiService.getJITConfig(customerId);
      // customers.jit_enabled, mirrored onto the config response as `jitEnabled`. This
      // is the master switch every backend gate reads — not the `enabled` field on the
      // same response, which is the separate jit_configurations row. Absent (older
      // backend) is read as enabled, matching App.js.
      setJitEnabled(response?.data?.jitEnabled !== false);
      setForbidden(false);
      setLoadError(null);
    } catch (err) {
      if (isRoleForbidden(err)) {
        setForbidden(true);
      } else {
        setLoadError(err.message || 'Failed to load JIT access settings');
      }
      setJitEnabled(null);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  // The switch is never flipped optimistically: it renders `jitEnabled`, which only
  // moves once the backend has confirmed the write. A failed PUT therefore leaves the
  // control showing the state that is actually in force.
  const applyChange = async (nextEnabled) => {
    setSaving(true);
    try {
      // The request body field is `enabled` (JITConfigurationUpdate.Enabled) while the
      // response field is `jitEnabled`. They are deliberately different names for the
      // same tenant switch: the handler writes customers.jit_enabled AND
      // jit_configurations.enabled from this one field.
      const response = await apiService.updateJITConfig(customerId, { enabled: nextEnabled });
      const applied = response?.data?.jitEnabled;
      setJitEnabled(typeof applied === 'boolean' ? applied : nextEnabled);
      toast.success(
        nextEnabled
          ? 'JIT access is now on for your organization'
          : 'JIT access is now off for your organization'
      );
    } catch (err) {
      if (isRoleForbidden(err)) {
        setForbidden(true);
        toast.error('The SSO Admin role is required to change JIT access');
      } else {
        toast.error(err.message || 'Failed to update JIT access');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = (event) => {
    if (event.target.checked) {
      applyChange(true);
      return;
    }
    // Turning JIT off expires the pending queue and cannot be undone by turning it
    // back on, so it goes through a confirmation step.
    setShowDisableConfirm(true);
  };

  const handleConfirmDisable = async () => {
    setShowDisableConfirm(false);
    await applyChange(false);
  };

  const renderBody = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <CircularProgress sx={{ color: accentColor }} />
        </Box>
      );
    }

    if (forbidden) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <LockIcon sx={{ fontSize: 48, color: '#E5E5E5', mb: 1 }} />
          <Typography
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              fontSize: '0.95rem',
              color: '#253E66',
              mb: 1,
            }}
          >
            SSO Admin role required
          </Typography>
          <Typography sx={{ ...bodyTextSx, maxWidth: 520, mx: 'auto' }}>
            JIT access settings can only be viewed and changed by an admin with the
            SSO Admin role. Ask an SSO admin in your organization to make this change.
          </Typography>
        </Box>
      );
    }

    if (loadError) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <ErrorOutlineIcon sx={{ fontSize: 48, color: '#E5E5E5', mb: 1 }} />
          <Typography sx={{ ...bodyTextSx, mb: 2, maxWidth: 520, mx: 'auto' }}>
            {loadError}
          </Typography>
          <Button variant="outlined" onClick={fetchConfig} sx={buttonStyles.cancel}>
            Try Again
          </Button>
        </Box>
      );
    }

    return (
      <>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 3,
            backgroundColor: '#F9FBFD',
            border: '1px solid #E5E5E5',
            borderRadius: '6px',
            p: 2.5,
          }}
        >
          <Box>
            <Typography
              sx={{
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 600,
                fontSize: '0.95rem',
                color: '#253E66',
                mb: 0.5,
              }}
            >
              JIT access for your organization
            </Typography>
            <Typography sx={bodyTextSx}>
              When on, your users can request time-bound access to AWS accounts and
              approvers can grant it.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
            {saving && <CircularProgress size={18} sx={{ color: accentColor }} />}
            <Typography
              sx={{
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 600,
                fontSize: '0.875rem',
                color: jitEnabled ? accentColor : '#666666',
              }}
            >
              {jitEnabled ? 'On' : 'Off'}
            </Typography>
            <Switch
              checked={!!jitEnabled}
              disabled={saving}
              onChange={handleToggle}
              inputProps={{ 'aria-label': 'JIT access for your organization' }}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: accentColor,
                  '&:hover': { backgroundColor: `${accentColor}15` },
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: accentColor,
                },
              }}
            />
          </Box>
        </Box>

        {!jitEnabled && (
          <Alert
            severity="info"
            icon={<AccessTimeIcon fontSize="inherit" />}
            sx={{
              mt: 2.5,
              backgroundColor: '#F9FBFD',
              border: '1px solid #E5E5E5',
              borderRadius: '6px',
              '& .MuiAlert-icon': { color: '#5D6D7E' },
              '& .MuiAlert-message': {
                fontFamily: 'Metropolis, sans-serif',
                color: '#253E66',
              },
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              JIT access is off
            </Typography>
            <Typography variant="body2" sx={{ color: '#666666' }}>
              Your users cannot raise new JIT requests while it is off. Access that was
              already granted is unaffected — it stays active, remains visible in the JIT
              portal, and expires on its normal schedule. Turn it back on at any time.
            </Typography>
          </Alert>
        )}
      </>
    );
  };

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      style={{
        minHeight: '100vh',
        paddingTop: 32,
        paddingBottom: 32,
        paddingLeft: 32,
        paddingRight: 32,
        backgroundColor: '#F9FBFD',
      }}
    >
      {/* Page Header */}
      <motion.div variants={sectionVariants} style={{ marginBottom: 24 }}>
        <Paper
          elevation={0}
          sx={{
            backgroundColor: '#FFFFFF',
            borderRadius: '8px',
            border: '1px solid #E5E5E5',
            p: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              sx={{
                backgroundColor: accentColor,
                width: 48,
                height: 48,
              }}
            >
              <AccessTimeIcon sx={{ fontSize: 24 }} />
            </Avatar>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontFamily: 'Bitter, Georgia, serif',
                  fontWeight: 600,
                  fontSize: '1.75rem',
                  color: '#253E66',
                }}
              >
                JIT Access
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontSize: '0.875rem',
                  color: '#666666',
                  fontWeight: 500,
                }}
              >
                Turn Just-In-Time access on or off for your organization
              </Typography>
            </Box>
          </Box>
        </Paper>
      </motion.div>

      {/* Main Content */}
      <motion.div variants={sectionVariants}>
        <Card elevation={0} sx={cardStyle}>
          <CardContent sx={{ p: 3 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar
                  sx={{
                    bgcolor: accentColor,
                    width: 40,
                    height: 40,
                  }}
                >
                  <AccessTimeIcon sx={{ fontSize: 20 }} />
                </Avatar>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 600,
                    fontSize: '1rem',
                    color: '#253E66',
                  }}
                >
                  Just-In-Time Access
                </Typography>
              </Box>
              {!loading && !forbidden && !loadError && (
                <Chip
                  label={jitEnabled ? 'Enabled' : 'Disabled'}
                  size="small"
                  sx={{
                    backgroundColor: jitEnabled ? accentColorLight : '#F4F6F7',
                    color: jitEnabled ? accentColor : '#666666',
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                  }}
                />
              )}
            </Box>

            <Divider sx={{ mb: 2.5, borderColor: '#E5E5E5' }} />

            {renderBody()}
          </CardContent>
        </Card>
      </motion.div>

      {/* Turn Off Confirmation Dialog */}
      <Dialog
        open={showDisableConfirm}
        onClose={() => setShowDisableConfirm(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={dialogPaperProps}
      >
        <DialogTitle
          sx={{
            backgroundColor: '#FEF9E7',
            borderBottom: '1px solid #E5E5E5',
            fontFamily: 'Bitter, Georgia, serif',
            fontWeight: 600,
            color: '#253E66',
          }}
        >
          Turn off JIT access?
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <Alert
              severity="warning"
              sx={{
                mb: 2.5,
                backgroundColor: '#FEF9E7',
                border: '1px solid #F39C1230',
                borderRadius: '6px',
                '& .MuiAlert-icon': { color: '#F39C12' },
                '& .MuiAlert-message': {
                  fontFamily: 'Metropolis, sans-serif',
                  color: '#253E66',
                },
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                This is not an emergency revocation. Access that has already been granted
                is not withdrawn.
              </Typography>
            </Alert>

            <Typography
              sx={{
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 600,
                fontSize: '0.875rem',
                color: '#253E66',
                mb: 1.25,
              }}
            >
              What changes from now on
            </Typography>
            <ConsequenceItem>
              No new JIT requests can be created.
            </ConsequenceItem>
            <ConsequenceItem>
              No pending requests can be approved. Requests still waiting for approval are
              expired straight away, and turning JIT back on does not bring them back —
              they have to be raised again.
            </ConsequenceItem>

            <Typography
              sx={{
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 600,
                fontSize: '0.875rem',
                color: '#253E66',
                mt: 2.5,
                mb: 1.25,
              }}
            >
              What does not change
            </Typography>
            <ConsequenceItem>
              Access that has already been granted stays active. It remains visible in the
              JIT portal and expires on its normal schedule.
            </ConsequenceItem>
            <ConsequenceItem>
              Users can still revoke their own active sessions at any time.
            </ConsequenceItem>

            <Typography sx={{ ...bodyTextSx, fontSize: '0.8rem', color: '#999999', mt: 2.5 }}>
              You can turn JIT access back on whenever you need it.
            </Typography>
          </motion.div>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 2 }}>
          <Button
            onClick={() => setShowDisableConfirm(false)}
            variant="outlined"
            sx={buttonStyles.cancel}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDisable}
            disabled={saving}
            variant="contained"
            sx={buttonStyles.primary(accentColor, accentColorHover)}
          >
            Turn Off JIT Access
          </Button>
        </DialogActions>
      </Dialog>
    </motion.div>
  );
};

export default JITSettings;
